import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useChartData } from "./quantStore";
import { computeQuant } from "./quantStats";
import { calculateKaufmanEfficiencyRatio } from "./indicatorCalculations";
import { palette } from "../pages/pageStyles";
import { useAuth } from "../auth/AuthContext";
import { MARKET_API_BASE, marketFetch } from "../backendConfig";
import "./QuantChrome.css";

export const QUANT_STRIP_HEIGHT = 64;
const WINDOW = 200;

const signed = (v, d) => (v >= 0 ? "+" : "−") + Math.abs(v).toFixed(d);
const pct = (v, d) => (v >= 0 ? "+" : "−") + Math.abs(v * 100).toFixed(d) + "%";

/** Readings across the top of the chart page, from the last 200 candles on the chart. */
export default function QuantStrip({ panelOpen, onTogglePanel }) {
  const { user } = useAuth();
  const [feedInfo, setFeedInfo] = useState(null);
  const isAdmin = Boolean(user?.is_admin);
  const data = useChartData();
  const q = useMemo(
    () => computeQuant(data.candles, { window: WINDOW, barSeconds: data.barSeconds }),
    // version changes whenever the chart publishes new candles
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.version]
  );
  const regimePeriod = Number(data.regimePeriod ?? 20);
  const regimeSmooth = Number(data.regimeSmooth ?? 5);
  const regimeSeries = useMemo(() => calculateKaufmanEfficiencyRatio(data.candles, regimePeriod, regimeSmooth), [data.candles, regimePeriod, regimeSmooth]);
  const trendThreshold = Number(data.regimeTrendThreshold ?? 0.4);
  const rangeThreshold = Number(data.regimeRangeThreshold ?? 0.25);
  const regimeScore = regimeSeries[regimeSeries.length - 1]?.value ?? q?.efficiency ?? 0;
  const currentRegime = data.regimeModel === "hmm"
    ? data.regimeCurrent || "Warming up"
    : regimeScore >= trendThreshold ? "Trend" : regimeScore <= rangeThreshold ? "Range" : "Mixed";

  useEffect(() => {
    if (!user) {
      setFeedInfo(null);
      return undefined;
    }
    let stopped = false;
    let timer = null;
    const refresh = async () => {
      try {
        const response = await marketFetch(`${MARKET_API_BASE}/market/info`);
        if (!response.ok) throw new Error(`Feed status ${response.status}`);
        const next = await response.json();
        if (!stopped) setFeedInfo(next);
      } catch {
        if (!stopped) setFeedInfo(null);
      }
    };
    refresh();
    timer = window.setInterval(refresh, 30000);
    return () => {
      stopped = true;
      if (timer !== null) window.clearInterval(timer);
    };
  }, [user]);

  const feedMode = feedInfo?.is_simulated
    ? "SIMULATED"
    : feedInfo?.provider
      ? `${feedInfo.provider} ${String(feedInfo.data_mode || "").toUpperCase()}`.trim()
      : "FEED STATUS";
  const feedState = feedInfo?.connection_state === "connected"
    ? "Connected"
    : feedInfo?.connection_state === "simulated"
      ? "Sample values"
      : feedInfo?.connection_state === "quiet"
        ? "No recent quotes"
        : feedInfo?.connection_state === "waiting_for_quotes"
          ? "Waiting for quotes"
          : feedInfo?.connection_state === "not_initialized"
            ? "Connecting"
            : "Unavailable";
  const feedHelp = feedInfo
    ? [
        `${feedInfo.provider} · ${feedInfo.data_mode}`,
        `Connection: ${feedState}`,
        ...(feedInfo.last_quote_age_seconds != null ? [`Last quote ${feedInfo.last_quote_age_seconds}s ago`] : []),
        ...(feedInfo.notes || []),
      ].join("\n")
    : "Market feed status is unavailable.";

  const items = q
    ? [
        { k: "VOL (YEAR)", v: `${(q.annVol * 100).toFixed(1)}%`, sub: `realised, ${q.bars} bars`, help: "Spread of returns, scaled to a year using how many bars this market really has per year" },
        { k: "Z-SCORE 20", v: signed(q.z20, 2), sub: Math.abs(q.z20) > 2 ? "stretched" : "normal", c: Math.abs(q.z20) > 2 ? palette.highlight : undefined, help: "How many standard deviations the price is from its 20-bar average" },
        { k: "REGIME", v: currentRegime, sub: data.regimeModel === "hmm"
          ? Number.isFinite(data.regimeValidation) ? `HMM · holdout Δ ${data.regimeValidation >= 0 ? "+" : ""}${data.regimeValidation.toFixed(2)}` : "HMM · load more candles"
          : `Trend ≥ ${trendThreshold.toFixed(2)} · Range ≤ ${rangeThreshold.toFixed(2)}`, c: palette.glow,
        help: data.regimeModel === "hmm"
          ? "HMM fits the first 70% of loaded candles and forward-filters the last 30%. Its score compares with a one-state baseline and does not measure trading performance."
          : "Market state uses the admin-set trend and range cutoffs; intermediate values are mixed" },
        { k: "SHARPE (YEAR)", v: signed(q.sharpe, 2), sub: `last ${q.bars} bars`, c: q.sharpe >= 0 ? palette.up : palette.down, help: "Average return divided by its spread, scaled to a year. Short windows swing a lot." },
        { k: "AUTOCORR 1", v: signed(q.autocorr, 2), sub: q.autocorr > 0.1 ? "momentum" : q.autocorr < -0.1 ? "mean-reverting" : "random walk", help: "Correlation of each return with the one before it" },
        { k: "SKEW", v: signed(q.skew, 2), sub: `kurtosis ${signed(q.kurtosis, 1)}`, help: "Lopsidedness of returns, and how heavy the tails are versus a normal curve" },
        { k: "MAX DRAWDOWN", v: pct(q.maxDrawdown, 2), sub: `last ${q.bars} bars`, c: palette.down, help: "Largest fall from a high within the window" },
        { k: "RSI 14", v: q.rsi14 === null ? "-" : q.rsi14.toFixed(1), sub: q.rsi14 > 70 ? "overbought" : q.rsi14 < 30 ? "oversold" : "neutral", help: "Relative strength index over 14 bars" },
      ].filter((item) => isAdmin || item.k !== "REGIME")
    : [];

  return (
    <div
      role="group"
      aria-label="Quant readings"
      className="lwt-quant-strip"
      style={{
        height: QUANT_STRIP_HEIGHT,
        display: "flex",
        alignItems: "stretch",
        overflowX: "auto",
        border: `1px solid ${palette.border}`,
        borderRadius: 6,
        background: palette.glass,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      {q ? (
        items.map((m) => {
          const style = { flex: "1 0 118px", minWidth: 118, padding: "8px 14px", borderRight: `1px solid ${palette.border}`, textAlign: "left" };
          const contents = (
            <>
              <div className="lwt-label" style={{ fontSize: 11, color: palette.mutedText }}>{m.k}</div>
              <div className="lwt-num" style={{ fontSize: 16, fontWeight: 400, color: m.c || palette.text, marginTop: 1, whiteSpace: "nowrap" }}>{m.v}</div>
              <div style={{ fontSize: 11, color: palette.mutedText, whiteSpace: "nowrap" }}>{m.sub}</div>
            </>
          );
          return m.k === "REGIME" && isAdmin ? (
            <button key={m.k} type="button" title="Click to show or hide historical Trend/Range areas; change cutoffs in Kaufman Range settings" aria-label={`Toggle historical Trend and Range areas; current market state is ${m.v}`} onClick={() => window.dispatchEvent(new Event("lwt:toggle-regime-zones"))} style={{ ...style, border: 0, borderRight: `1px solid ${palette.border}`, background: "transparent", color: palette.text, font: "inherit", cursor: "pointer" }}>
              {contents}
            </button>
          ) : (
            <div key={m.k} title={m.help} style={style}>
              {contents}
            </div>
          );
        })
      ) : (
        <div style={{ flex: 1, display: "flex", alignItems: "center", padding: "0 16px", fontSize: 13, color: palette.mutedText }}>
          Quant readings appear once the chart shows at least 31 candles.
        </div>
      )}
      <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, padding: "0 10px" }}>
        <div
          role="status"
          aria-label={`Market feed: ${feedMode}, ${feedState}`}
          title={feedHelp}
          style={{ display: "grid", alignContent: "center", minWidth: 126, padding: "4px 8px", border: `1px solid ${palette.border}`, borderRadius: 4, color: feedInfo?.is_simulated ? palette.highlight : palette.text, fontSize: 10, lineHeight: 1.25, whiteSpace: "nowrap" }}
        >
          <strong style={{ fontWeight: 600 }}>{feedMode}</strong>
          <span style={{ color: palette.mutedText }}>{feedState}</span>
        </div>
        <button
          type="button"
          onClick={onTogglePanel}
          aria-pressed={panelOpen}
          aria-label={panelOpen ? "Hide quantitative panel" : "Show quantitative panel"}
          title={panelOpen ? "Hide quantitative panel" : "Show quantitative panel"}
          className="lwt-label"
          style={{
            display: "grid",
            placeItems: "center",
            width: 36,
            minHeight: 36,
            padding: 0,
            borderRadius: 8,
            border: `1px solid ${panelOpen ? palette.glow : palette.border}`,
            background: panelOpen ? palette.selected : "transparent",
            color: panelOpen ? palette.accentBlueSoft : palette.mutedText,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2.5" />
            <path d="M14.5 4v16M7 9h3M7 13h3M7 17h3" />
          </svg>
        </button>
      </div>
    </div>
  );
}
